import {useEffect, useState} from 'react'
import {useLayout} from '../../../../_metronic/layout/core'
import {ProductCategory, createCategory, editCategory} from './api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'

// ─── Validation Schema ────────────────────────────────────────────────────────

const categorySchema = Yup.object().shape({
  category_name: Yup.string()
    .required('Category name is required')
    .matches(/^[a-zA-Z0-9\-' ]{1,50}$/, {
      message:
        'Category name must be 1-50 characters and can only contain letters, numbers, hyphens, apostrophes and spaces',
    }),
  abbreviation: Yup.string()
    .required('Abbreviation is required')
    .matches(/^[a-zA-Z0-9]{1,10}$/, {
      message: 'Abbreviation must be 1-10 alphanumeric characters',
    }),
})

// ─── Types ────────────────────────────────────────────────────────────────────

interface CategoryFormValues {
  category_name: string
  abbreviation: string
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateEditCategory: React.FC<{
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>
  setEditCategory: React.Dispatch<React.SetStateAction<ProductCategory | undefined>>
  editCategoryData?: ProductCategory
  setShouldRefreshCategories: React.Dispatch<React.SetStateAction<boolean>>
}> = ({setShowForm, editCategoryData, setShouldRefreshCategories, setEditCategory}) => {
  const {company} = useLayout()
  const [loading, setLoading] = useState(false)

  const initialValues: CategoryFormValues = {
    category_name: editCategoryData?.category_name || '',
    abbreviation: editCategoryData?.abbreviation || '',
  }

  const formik = useFormik<CategoryFormValues>({
    initialValues,
    validationSchema: categorySchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setLoading(true)
      const body = {
        company_id: company?.value,
        category_name: values.category_name,
        abbreviation: values.abbreviation,
        previous_category_name: editCategoryData?.category_name,
      }

      if (!editCategoryData) {
        await createCategory(body)
          .then((res) => {
            res?.detail && toast.success('The requested category has been added successfully.')
            formik.resetForm()
            setShouldRefreshCategories(true)
          })
          .catch((err) => toast.error(err?.detail || err.message))
          .finally(() => {
            setLoading(false)
            setShowForm(false)
          })
      } else {
        await editCategory(body)
          .then((res) => {
            res?.detail && toast.success('Category has been updated successfully.')
            formik.resetForm()
            setShouldRefreshCategories(true)
          })
          .catch((err) => toast.error(err?.detail || err.message))
          .finally(() => {
            setLoading(false)
            setShowForm(false)
          })
      }
    },
  })

  // Pre-populate fields when editing
  useEffect(() => {
    if (editCategoryData) {
      formik.setFieldValue('category_name', editCategoryData.category_name)
      formik.setFieldValue('abbreviation', editCategoryData.abbreviation)
    }
  }, [editCategoryData])

  return (
    <div className='card'>
      <div
        className='card-header border-0'
        data-bs-target='#kt_product_category_form'
        aria-expanded='true'
        aria-controls='kt_product_category_form'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>
            {!!editCategoryData ? 'Update Category' : 'Add New Category'}
          </h3>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div id='kt_product_category_form' className='collapse show'>
          <div className='p-8 pb-0'>
            {/* Category Name */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Category Name</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Category Name'
                  {...formik.getFieldProps('category_name')}
                />
                {formik.touched.category_name && formik.errors.category_name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.category_name}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Abbreviation */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Abbreviation</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Abbreviation'
                  {...formik.getFieldProps('abbreviation')}
                />
                {formik.touched.abbreviation && formik.errors.abbreviation && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.abbreviation}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end px-9 border-0 pb-7 pt-0'>
          <button
            type='button'
            className='btn btn-secondary'
            style={{marginRight: '10px'}}
            onClick={() => {
              formik.resetForm()
              setEditCategory(undefined)
              setShowForm(false)
              setShouldRefreshCategories(false)
            }}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn-primary ml-2'
            disabled={loading || !formik.isValid}
          >
            {!loading && !!editCategoryData ? 'Save Category' : 'Add Category'}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
